import React from 'react';
import { shuffle } from 'lodash';
import { Box, IconButton, Stack } from '@mui/material';
import { theme } from '../../../../../../common/styles/theme';
import { Text } from '../../../../../../common/components/Text/Text';
import { Paper } from '../../../../../../common/components/Paper/Paper';
import { SpeakerIcon } from '../../../../../../../icons/SpeakerIcon';
import { textSpeaker } from '../../../../../effects/textSpeaker';
import { NativeTooltip } from '../../NativeTooltip/NativeTooltip';
import { Tabs } from '../../../../../../common/components/Tabs/Tabs';
import { Tab } from '../../../../../../common/components/Tab/Tab';
import { pillTabsSx } from '../../../../../../common/components/Tabs/pillTabsSx';
import { IRestExerciseAnswer, TExerciseMatchItem, TExerciseNative } from '../../../../../../common/rest/exercises/exercisesRest';

// what the right column shows: the meaning in the lesson language, that meaning in the viewer's own, or the word's translation
type TMatchRightMode = 'meaning' | 'meaningNative' | 'wordNative';

interface IExerciseMatchProps {
	instruction?: string;
	// the instruction in the viewer's native language
	instructionNative?: string;
	// the pairs in the viewer's native language, index-aligned with items[].pairs; none — no mode switch
	setsNative?: TExerciseNative['sets'];
	items: TExerciseMatchItem[];
	answers: Record<number, IRestExerciseAnswer>;
	langCode?: string;
	// viewing another participant's answers — nothing here may be picked
	readOnly?: boolean;
	// identifies whose progress is being shown ('me' or a student id) — a change here must reset local match state
	viewKey: string;
	onAnswer: (questionIndex: number, order: number[]) => void;
}

const SpeakButton: React.FC<{ text: string, langCode?: string }> = ({ text, langCode }) => (
	<IconButton
		size={'small'}
		onClick={(e) => { e.stopPropagation(); textSpeaker.speak(text, langCode); }}
		sx={{ color: theme.palette.grey[400], flexShrink: 0 }}
	>
		<SpeakerIcon sx={{ width: theme.spacing(2), height: theme.spacing(2) }} />
	</IconButton>
);

// One matching set: tap a word, then tap its meaning. A wrong pair flashes red and resets — you
// can't finish with a wrong pair, so completing the set always means every pair is correct.
const MatchSet: React.FC<{
	item: TExerciseMatchItem;
	questionIndex: number;
	answer?: IRestExerciseAnswer;
	// the right column's text per pair, in the chosen mode
	rightTexts: string[];
	langCode?: string;
	readOnly?: boolean;
	onAnswer: (questionIndex: number, order: number[]) => void;
}> = ({ item, questionIndex, answer, rightTexts, langCode, readOnly, onAnswer }) => {
	const isDone = !!answer;
	const interactive = !isDone && !readOnly;
	// the caller remounts this whole component (via key) on any relevant change, so this only
	// needs to be right on first mount — a reset effect would leave one stale render first
	const [meaningOrder] = React.useState(() => shuffle(item.pairs.map((_, i) => i)));
	const [selectedWord, setSelectedWord] = React.useState<number | null>(null);
	const [matched, setMatched] = React.useState<Set<number>>(new Set());
	const [wrongPair, setWrongPair] = React.useState<{ word: number, meaning: number } | null>(null);

	const pickWord = (wordIndex: number) => {
		if (!interactive || matched.has(wordIndex) || wrongPair) return;
		setSelectedWord(wordIndex);
	};

	const pickMeaning = (pairIndex: number) => {
		if (!interactive || selectedWord === null || matched.has(pairIndex) || wrongPair) return;
		if (selectedWord === pairIndex) {
			const next = new Set(matched);
			next.add(selectedWord);
			setMatched(next);
			setSelectedWord(null);
			if (next.size === item.pairs.length) {
				onAnswer(questionIndex, item.pairs.map((_, i) => i));
			}
		} else {
			setWrongPair({ word: selectedWord, meaning: pairIndex });
			setTimeout(() => {
				setWrongPair(null);
				setSelectedWord(null);
			}, 500);
		}
	};

	const rowStyle = (state: 'done' | 'selected' | 'wrong' | 'idle') => ({
		p: theme.spacing(.75, 1),
		borderRadius: theme.spacing(0.5),
		display: 'flex',
		alignItems: 'center',
		cursor: interactive && (state === 'idle' || state === 'selected') ? 'pointer' : 'default',
		background: {
			done: theme.palette.success.main + '22',
			selected: theme.palette.warning.light,
			wrong: theme.palette.error.main + '22',
			idle: theme.palette.common.white,
		}[state],
	});

	// done: nothing left to match — each word next to its meaning, rows aligned at the top
	if (isDone) {
		return (
			<Paper sx={{ p: theme.spacing(1.5), background: theme.palette.grey[50] }}>
				<Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 2, rowGap: .75 }}>
					{item.pairs.map((pair, pairIndex) => (
						<React.Fragment key={pairIndex}>
							{/* stretch: the word box takes the row's height, the same as its meaning's */}
							<Stack direction={'row'} alignItems={'stretch'} gap={.5}>
								<Box sx={{ alignSelf: 'center' }}>
									<SpeakButton text={pair.word} langCode={langCode} />
								</Box>
								<Box sx={{ ...rowStyle('done'), flex: '1 1 auto' }}>
									<Text sx={{ flex: '1 1 auto' }}>{pair.word}</Text>
								</Box>
							</Stack>
							<Box sx={rowStyle('done')}>
								<Text sx={{ flex: '1 1 auto' }}>{rightTexts[pairIndex]}</Text>
							</Box>
						</React.Fragment>
					))}
				</Box>
			</Paper>
		);
	}

	return (
		<Paper sx={{ p: theme.spacing(1.5), background: theme.palette.grey[50] }}>
			<Stack direction={'row'} gap={2}>
				<Stack gap={.75} sx={{ flex: '1 1 0' }}>
					{item.pairs.map((pair, wordIndex) => (
						// speaker outside the word box, on the left — as in the other exercises; a click on it doesn't pick the word
						<Stack key={wordIndex} direction={'row'} alignItems={'center'} gap={.5}>
							<SpeakButton text={pair.word} langCode={langCode} />
							<Box
								onClick={() => pickWord(wordIndex)}
								sx={{
									...rowStyle(matched.has(wordIndex) ? 'done' : selectedWord === wordIndex ? 'selected' : wrongPair?.word === wordIndex ? 'wrong' : 'idle'),
									flex: '1 1 auto',
								}}
							>
								<Text sx={{ flex: '1 1 auto' }}>{pair.word}</Text>
							</Box>
						</Stack>
					))}
				</Stack>
				<Stack gap={.75} sx={{ flex: '1 1 0' }}>
					{meaningOrder.map(pairIndex => (
						<Box
							key={pairIndex}
							onClick={() => pickMeaning(pairIndex)}
							sx={rowStyle(matched.has(pairIndex) ? 'done' : wrongPair?.meaning === pairIndex ? 'wrong' : 'idle')}
						>
							<Text sx={{ flex: '1 1 auto' }}>{rightTexts[pairIndex]}</Text>
						</Box>
					))}
				</Stack>
			</Stack>
		</Paper>
	);
};

export const ExerciseMatch: React.FC<IExerciseMatchProps> = ({ instruction, instructionNative, setsNative, items, answers, langCode, readOnly, viewKey, onAnswer }) => {

	// not remembered: every exercise opens on the meaning in the lesson language
	const [mode, setMode] = React.useState<TMatchRightMode>('meaning');
	const rightMode = setsNative ? mode : 'meaning';

	return (
		<Stack gap={1.5}>
			{instruction &&
				<Box sx={{ pl: theme.spacing(1) }}>
					<NativeTooltip text={instructionNative}>
						<Text
							component={'span'}
							sx={{
								fontSize: theme.typography.subtitle2,
							}}
						>
							{instruction}
						</Text>
					</NativeTooltip>
				</Box>
			}
			{!!setsNative &&
				<Tabs
					value={rightMode}
					onChange={(_, value: TMatchRightMode) => setMode(value)}
					// wraps on a narrow screen instead of clipping a pill
					sx={{ ...pillTabsSx, '.MuiTabs-scroller': { whiteSpace: 'normal' }, '.MuiTabs-flexContainer': { flexWrap: 'wrap' } }}
				>
					<Tab label={'Meaning'} value={'meaning'} />
					<Tab label={'In my language'} value={'meaningNative'} />
					<Tab label={'Translation'} value={'wordNative'} />
				</Tabs>
			}
			{items.map((item, questionIndex) => {
				const answer = answers[questionIndex];
				// forces a remount on regenerate, viewer switch, or answer appearing/clearing — a reset
				// effect can't do this without a stale first render (exercise.id stays the same on regenerate)
				const resetKey = `${viewKey}:${item.pairs.map(p => p.word).join('|')}:${answer ? 'done' : 'pending'}`;
				// a missing translation falls back to the meaning; switching modes keeps the set's progress
				const rightTexts = item.pairs.map((pair, i) => (rightMode !== 'meaning' && setsNative?.[questionIndex]?.[i]?.[rightMode]) || pair.meaning);
				return (
					<MatchSet
						key={resetKey}
						item={item}
						questionIndex={questionIndex}
						answer={answer}
						rightTexts={rightTexts}
						langCode={langCode}
						readOnly={readOnly}
						onAnswer={onAnswer}
					/>
				);
			})}
		</Stack>
	);
};
